<template>
  <div
    v-if="open"
    class="flex h-full w-full flex-col bg-white overflow-hidden transition-all duration-300"
    role="region"
    aria-label="AI Section Editor"
  >
    <!-- Header -->
    <div class="flex items-center justify-between border-b border-[#e2e8f0] px-5 py-3.5 bg-gradient-to-r from-[#f8fbff] to-[#f0f7ff]">
      <div class="flex items-center gap-3">
        <div class="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-tr from-[#0f3557] to-[#2563eb] text-white shadow-sm shrink-0">
          <Sparkles class="h-4.5 w-4.5 animate-pulse" />
        </div>
        <div class="min-w-0">
          <h3 class="text-sm font-extrabold text-[#0f172a] truncate">
            {{ sectionTitle }}
          </h3>
          <p class="text-[11px] text-[#64748b]">
            Revise section narrative with AI
          </p>
        </div>
      </div>

      <button
        type="button"
        class="flex h-8 w-8 items-center justify-center rounded-xl text-[#64748b] hover:bg-[#e2e8f0] hover:text-[#0f172a] transition shrink-0"
        title="Close AI Editor"
        @click="handleClose"
      >
        <X class="h-4 w-4" />
      </button>
    </div>

    <!-- Body Content -->
    <div class="flex-1 overflow-y-auto p-4 space-y-4">
      <!-- Published report warning -->
      <div
        v-if="isPublished"
        class="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50/90 p-3 text-[11px] text-amber-900"
      >
        <AlertCircle class="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <span class="font-bold">Published Report:</span>
          Revisions applied here will update the live report document and PDF.
        </div>
      </div>

      <!-- Model Selection -->
      <div class="space-y-1">
        <div class="flex items-center justify-between">
          <label class="text-[11px] font-extrabold text-[#334155] uppercase tracking-wider">
            AI Engine & Model
          </label>
          <span
            v-if="hasOllama"
            class="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 border border-emerald-200"
          >
            <span class="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-ping" />
            Ollama Online
          </span>
        </div>

        <div class="relative">
          <select
            v-model="selectedModelId"
            :disabled="isProcessing"
            class="w-full appearance-none rounded-xl border border-[#cbd5e1] bg-white px-3 py-2 text-xs font-semibold text-[#0f172a] shadow-sm transition focus:border-[#2563eb] focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 disabled:bg-slate-50"
          >
            <optgroup
              v-if="openAiModels.length > 0"
              label="Cloud OpenAI Models"
            >
              <option
                v-for="m in openAiModels"
                :key="m.id"
                :value="m.id"
              >
                {{ m.name }}
              </option>
            </optgroup>
            <optgroup
              v-if="ollamaModels.length > 0"
              label="Local Ollama Models"
            >
              <option
                v-for="m in ollamaModels"
                :key="m.id"
                :value="m.id"
              >
                {{ m.name }}
              </option>
            </optgroup>
          </select>
          <ChevronDown class="pointer-events-none absolute right-3 top-2.5 h-3.5 w-3.5 text-[#64748b]" />
        </div>
      </div>

      <!-- Client Brand Voice Accordion -->
      <div class="rounded-xl border border-[#e2e8f0] bg-[#fafcff] overflow-hidden">
        <button
          type="button"
          class="flex w-full items-center justify-between px-3.5 py-2.5 text-left text-xs font-bold text-[#334155] hover:bg-[#f1f5f9] transition"
          @click="showBrandVoice = !showBrandVoice"
        >
          <div class="flex items-center gap-1.5">
            <Volume2 class="h-3.5 w-3.5 text-[#2563eb]" />
            <span>Brand Voice & Tone</span>
            <span
              v-if="brandVoice"
              class="rounded bg-[#dbeafe] px-1.5 py-0.2 text-[9.5px] text-[#1e40af]"
            >Custom</span>
          </div>
          <ChevronDown
            class="h-3.5 w-3.5 text-[#64748b] transition-transform duration-200"
            :class="{ 'rotate-180': showBrandVoice }"
          />
        </button>

        <div
          v-show="showBrandVoice"
          class="border-t border-[#e2e8f0] p-3 space-y-2 bg-white"
        >
          <textarea
            v-model="brandVoice"
            rows="2"
            maxlength="500"
            placeholder="e.g. Formal tone, emphasize ROI, avoid jargon."
            class="w-full rounded-lg border border-[#cbd5e1] p-2 text-xs text-[#0f172a] focus:border-[#2563eb] focus:outline-none focus:ring-1 focus:ring-[#2563eb]"
          />
          <div class="flex items-center justify-end">
            <button
              type="button"
              :disabled="savingBrandVoice"
              class="inline-flex items-center gap-1 rounded-lg bg-[#0f3557] px-2.5 py-1 text-[11px] font-bold text-white transition hover:bg-[#1a4a6e] disabled:opacity-50"
              @click="saveBrandVoice"
            >
              <Save class="h-3 w-3" />
              <span>{{ savingBrandVoice ? 'Saving...' : 'Save Tone' }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Quick Suggestion Chips -->
      <div class="space-y-2">
        <div>
          <label class="text-[10px] font-extrabold text-[#64748b] uppercase tracking-wider block mb-1">
            Writing & Content
          </label>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="(chip, idx) in narrativeChips"
              :key="'narrative-' + idx"
              type="button"
              :disabled="isProcessing"
              class="inline-flex items-center gap-1 rounded-lg border border-[#d8e4f0] bg-white px-2 py-1 text-[11px] font-semibold text-[#334155] transition hover:border-[#2563eb] hover:bg-[#eff6ff] hover:text-[#2563eb] active:scale-95 disabled:opacity-50"
              @click="applyQuickChip(chip)"
            >
              <Sparkles class="h-2.5 w-2.5 text-[#2563eb]" />
              <span>{{ chip }}</span>
            </button>
          </div>
        </div>

        <div>
          <label class="text-[10px] font-extrabold text-[#64748b] uppercase tracking-wider block mb-1">
            Design, Layout & Themes
          </label>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="(chip, idx) in designChips"
              :key="'design-' + idx"
              type="button"
              :disabled="isProcessing"
              class="inline-flex items-center gap-1 rounded-lg border border-[#e0e7ff] bg-[#faf5ff] px-2 py-1 text-[11px] font-semibold text-[#6b21a8] transition hover:border-[#7c3aed] hover:bg-[#f3e8ff] hover:text-[#7c3aed] active:scale-95 disabled:opacity-50"
              @click="applyQuickChip(chip.prompt || chip.label)"
            >
              <LayoutGrid class="h-2.5 w-2.5 text-[#7c3aed]" />
              <span>{{ chip.label }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Revision Prompt Box -->
      <div class="space-y-1.5">
        <div class="flex items-center justify-between">
          <label class="text-[11px] font-extrabold text-[#334155] uppercase tracking-wider">
            Revision Prompt
          </label>
          <span class="text-[10px] text-[#94a3b8]">
            {{ promptText.length }}/1000
          </span>
        </div>

        <div class="relative">
          <!-- Floating Enhance with AI Button + Undo -->
          <div class="absolute right-2.5 top-2.5 z-10 flex items-center gap-1.5">
            <button
              v-if="originalPromptBeforeEnhance"
              type="button"
              class="inline-flex items-center gap-1 rounded-lg bg-slate-100 hover:bg-slate-200 border border-slate-300 px-2 py-1 text-[10px] font-semibold text-slate-700 shadow-xs transition active:scale-95"
              title="Undo enhanced prompt and revert to original"
              @click="handleUndoEnhance"
            >
              <RotateCcw class="h-2.5 w-2.5 text-slate-500" />
              <span>Undo</span>
            </button>
            <button
              type="button"
              :disabled="isProcessing || isEnhancing || !promptText.trim()"
              class="inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-[#2563eb] to-[#7c3aed] px-2.5 py-1 text-[10.5px] font-bold text-white shadow-xs transition hover:opacity-95 active:scale-95 disabled:opacity-30 disabled:pointer-events-none"
              title="Improve prompt with AI"
              @click="handleEnhancePrompt"
            >
              <Sparkles
                v-if="!isEnhancing"
                class="h-3 w-3 text-amber-300"
              />
              <Loader2
                v-else
                class="h-3 w-3 animate-spin"
              />
              <span>{{ isEnhancing ? 'Enhancing...' : 'Enhance' }}</span>
            </button>
          </div>

          <textarea
            v-model="promptText"
            rows="3"
            maxlength="1000"
            :disabled="isProcessing"
            placeholder="What should be revised in this section? (e.g. 2 columns with cards on left, or dark luxury theme)"
            class="w-full rounded-xl border border-[#cbd5e1] p-3 pr-28 text-xs text-[#0f172a] shadow-sm transition placeholder:text-[#94a3b8] focus:border-[#2563eb] focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 disabled:bg-slate-50"
            @keydown.ctrl.enter="handleApply"
            @keydown.meta.enter="handleApply"
          />
        </div>
      </div>

      <!-- Active Processing Banner -->
      <div
        v-if="isProcessing"
        class="flex items-center gap-3 rounded-xl border border-[#bfdbfe] bg-[#eff6ff] p-3.5 text-xs font-bold text-[#1e40af] animate-pulse"
      >
        <Loader2 class="h-4 w-4 animate-spin text-[#2563eb] shrink-0" />
        <div class="flex-1">
          <div>{{ processingStatusMessage }}</div>
          <div class="mt-0.5 text-[10px] font-normal text-[#3b82f6]">
            Validating numbers and updating preview
          </div>
        </div>
      </div>

      <!-- Error Alert Banner -->
      <div
        v-if="errorMessage"
        class="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-900"
      >
        <AlertTriangle class="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
        <div class="flex-1">
          <div class="font-bold">
            Revision Failed
          </div>
          <div class="mt-0.5 text-[11px] text-red-700">
            {{ errorMessage }}
          </div>
        </div>
        <button
          type="button"
          class="text-red-600 hover:text-red-900"
          @click="errorMessage = null"
        >
          <X class="h-3.5 w-3.5" />
        </button>
      </div>

      <!-- Fallback Alert Banner -->
      <div
        v-if="fallbackNotice"
        class="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
      >
        <Info class="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
        <div class="flex-1 text-[11px]">
          <span class="font-bold">Provider Fallback:</span>
          {{ fallbackNotice }}
        </div>
      </div>

      <!-- History & Restore Section -->
      <div class="border-t border-[#e2e8f0] pt-3 space-y-2">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-1.5 text-[11px] font-extrabold text-[#334155] uppercase tracking-wider">
            <History class="h-3 w-3 text-[#64748b]" />
            <span>Version History</span>
          </div>
          <div class="flex items-center gap-2">
            <button
              type="button"
              :disabled="isProcessing || history.length <= 0"
              class="text-[10.5px] font-bold text-[#dc2626] hover:underline disabled:opacity-40"
              @click="handleReset"
            >
              Reset to Original
            </button>
          </div>
        </div>

        <div
          v-if="loadingHistory"
          class="text-center py-2 text-xs text-[#94a3b8]"
        >
          Loading history...
        </div>

        <div
          v-else-if="history.length === 0"
          class="rounded-lg border border-dashed border-[#e2e8f0] p-2.5 text-center text-[11px] text-[#94a3b8]"
        >
          Original report data (no revisions yet)
        </div>

        <div
          v-else
          class="space-y-1.5 max-h-36 overflow-y-auto pr-1"
        >
          <div
            v-for="item in history"
            :key="item.id"
            class="flex items-center justify-between rounded-lg border p-2 text-xs transition"
            :class="item.is_active ? 'border-[#2563eb] bg-[#f0f7ff]' : 'border-[#e2e8f0] bg-white opacity-70'"
          >
            <div class="space-y-0.5 min-w-0 pr-2">
              <div class="flex items-center gap-1.5 font-bold text-[#0f172a] text-[11px]">
                <span>Version {{ item.version }}</span>
                <span
                  v-if="item.is_active"
                  class="rounded bg-[#2563eb] px-1 py-0.1 text-[9px] text-white"
                >Active</span>
                <span class="text-[10px] font-normal text-[#64748b]">via {{ item.model || item.provider || 'AI' }}</span>
              </div>
              <div
                class="text-[10.5px] text-[#64748b] truncate"
                :title="item.prompt"
              >
                "{{ item.prompt }}"
              </div>
            </div>

            <div class="flex items-center gap-2 shrink-0">
              <button
                v-if="!item.is_active"
                type="button"
                :disabled="isProcessing"
                class="inline-flex items-center gap-1 rounded-md border border-[#cbd5e1] bg-white px-2 py-0.5 text-[10px] font-bold text-[#0f3557] shadow-2xs transition hover:border-[#2563eb] hover:bg-[#eff6ff] hover:text-[#2563eb] active:scale-95 disabled:opacity-40"
                title="Restore to this version"
                @click="handleRestoreVersion(item.version)"
              >
                <RotateCcw class="h-2.5 w-2.5 text-[#2563eb]" />
                <span>Restore</span>
              </button>
              <span class="text-[9.5px] text-[#94a3b8]">
                {{ formatDate(item.created_at) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Actions -->
    <div class="border-t border-[#e2e8f0] bg-[#f8fbff] px-4 py-3">
      <div class="flex items-center justify-between gap-2">
        <button
          v-if="hasActiveOverride"
          type="button"
          :disabled="isProcessing"
          class="inline-flex items-center gap-1 rounded-xl border border-[#cbd5e1] bg-white px-3 py-2 text-xs font-bold text-[#334155] shadow-sm hover:bg-[#f1f5f9] transition active:scale-95 disabled:opacity-50"
          @click="handleUndo"
        >
          <RotateCcw class="h-3 w-3" />
          <span>Undo</span>
        </button>
        <div v-else />

        <div class="flex items-center gap-1.5">
          <button
            type="button"
            class="rounded-xl px-3 py-2 text-xs font-bold text-[#64748b] hover:text-[#0f172a] transition"
            @click="handleClose"
          >
            Cancel
          </button>
          <button
            type="button"
            :disabled="isProcessing || !promptText.trim()"
            class="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#0f3557] to-[#2563eb] px-4 py-2 text-xs font-bold text-white shadow-sm transition hover:brightness-110 active:scale-95 disabled:opacity-50 disabled:pointer-events-none"
            @click="handleApply"
          >
            <Sparkles class="h-3.5 w-3.5" />
            <span>{{ isProcessing ? 'Revising...' : 'Apply Revision' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- In-App Sleek Confirmation Dialog Overlay -->
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="confirmDialog.show"
        class="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs"
      >
        <div class="w-full max-w-xs rounded-2xl border border-[#e2e8f0] bg-white p-4 shadow-2xl space-y-3">
          <div class="flex items-start gap-3">
            <div
              class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
              :class="confirmDialog.confirmVariant === 'danger' ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-amber-50 text-amber-600 border border-amber-200'"
            >
              <AlertTriangle class="h-5 w-5" />
            </div>
            <div class="space-y-1 min-w-0">
              <h4 class="text-xs font-extrabold text-[#0f172a]">
                {{ confirmDialog.title }}
              </h4>
              <p class="text-[11px] leading-relaxed text-[#64748b]">
                {{ confirmDialog.description }}
              </p>
            </div>
          </div>

          <div class="flex items-center justify-end gap-2 pt-2 border-t border-[#f1f5f9]">
            <button
              type="button"
              class="rounded-xl px-3 py-1.5 text-xs font-semibold text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] transition"
              @click="confirmDialog.show = false"
            >
              Cancel
            </button>
            <button
              type="button"
              class="rounded-xl px-3.5 py-1.5 text-xs font-bold text-white shadow-sm transition active:scale-95"
              :class="confirmDialog.confirmVariant === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-[#0f3557] hover:bg-[#1a4a6e]'"
              @click="handleConfirmAction"
            >
              {{ confirmDialog.confirmLabel }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import {
  Sparkles,
  X,
  ChevronDown,
  Volume2,
  Save,
  Loader2,
  AlertTriangle,
  AlertCircle,
  Info,
  History,
  RotateCcw,
  LayoutGrid,
} from "lucide-vue-next";
import { useToast } from "vue-toast-notification";
import { aiSectionEditService } from "@/services/aiSectionEditService";
import type { AiModelItem, AiSectionHistoryItem } from "@/types/aiEditor";

const props = defineProps<{
  open: boolean;
  reportId: number | string | null;
  clientId: string | null;
  sectionKey: string;
  sectionLabel?: string;
  isPublished?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:open", value: boolean): void;
  (e: "section-updated", sectionKey: string): void;
}>();

const toast = useToast();

const availableModels = ref<AiModelItem[]>([]);
const selectedModelId = ref<string>("gpt-4o-mini");
const showBrandVoice = ref(false);
const brandVoice = ref("");
const savingBrandVoice = ref(false);

const promptText = ref("");
const isProcessing = ref(false);
const isEnhancing = ref(false);
const originalPromptBeforeEnhance = ref<string | null>(null);
const processingStatusMessage = ref("Queueing revision job...");
const errorMessage = ref<string | null>(null);
const fallbackNotice = ref<string | null>(null);

const history = ref<AiSectionHistoryItem[]>([]);
const loadingHistory = ref(false);

const confirmDialog = ref<{
  show: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  confirmVariant: "primary" | "danger" | "warning";
  onConfirm: (() => void) | null;
}>({
  show: false,
  title: "",
  description: "",
  confirmLabel: "Confirm",
  confirmVariant: "primary",
  onConfirm: null,
});

function openConfirm(options: {
  title: string;
  description: string;
  confirmLabel: string;
  confirmVariant?: "primary" | "danger" | "warning";
  onConfirm: () => void;
}) {
  confirmDialog.value = {
    show: true,
    title: options.title,
    description: options.description,
    confirmLabel: options.confirmLabel,
    confirmVariant: options.confirmVariant || "primary",
    onConfirm: options.onConfirm,
  };
}

function handleConfirmAction() {
  confirmDialog.value.show = false;
  const action = confirmDialog.value.onConfirm;
  if (action) {
    action();
  }
}

const narrativeChips = [
  "Make it punchy & concise",
  "Highlight ROI & wins",
  "Executive tone",
  "Growth strategy",
  "Simplify jargon",
];

const designChips = [
  {
    label: "2-Column Split",
    prompt: "redesign the UI, I want you to place the deliverable cards on the left side and the progress, insight, and recommendation on the right side. so it will be 2 columns",
  },
  {
    label: "Dark Luxury Theme",
    prompt: "change the theme for this section to a sleek dark navy luxury theme with vibrant accents",
  },
  {
    label: "Add Milestone Banner",
    prompt: "add a success highlight banner at the top showing: 'Milestone Achieved: All promotional collateral produced and approved on schedule'",
  },
  {
    label: "Default Layout",
    prompt: "reset the section layout back to the standard default grid",
  },
];

const sectionTitle = computed(() => {
  if (props.sectionLabel) return props.sectionLabel;
  return props.sectionKey
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());
});

const openAiModels = computed(() =>
  availableModels.value.filter((m) => m.provider === "openai")
);
const ollamaModels = computed(() =>
  availableModels.value.filter((m) => m.provider === "ollama")
);
const hasOllama = computed(() => ollamaModels.value.length > 0);

const hasActiveOverride = computed(() =>
  history.value.some((h) => h.is_active)
);

function applyQuickChip(chip: string) {
  if (!promptText.value.trim()) {
    promptText.value = chip;
  } else {
    promptText.value = `${promptText.value.trim()}. ${chip}`;
  }
}

function handleClose() {
  if (isProcessing.value) {
    openConfirm({
      title: "Cancel Revision?",
      description: "AI revision is currently in progress. Closing the editor will abandon this prompt.",
      confirmLabel: "Close Anyway",
      confirmVariant: "danger",
      onConfirm: () => emit("update:open", false),
    });
    return;
  }
  emit("update:open", false);
}

async function loadModels() {
  try {
    const res = await aiSectionEditService.getModels();
    availableModels.value = res.models || [];
    if (res.default_model && !selectedModelId.value) {
      selectedModelId.value = res.default_model;
    }
  } catch {
    availableModels.value = [
      { id: "gpt-4o-mini", name: "GPT-4o Mini (Fast)", provider: "openai", is_available: true },
      { id: "gpt-4o", name: "GPT-4o (High Quality)", provider: "openai", is_available: true },
      { id: "llama3:8b", name: "llama3:8b", provider: "ollama", is_available: true },
      { id: "gemma3:4b", name: "gemma3:4b", provider: "ollama", is_available: true },
      { id: "qwen2.5:7b", name: "qwen2.5:7b", provider: "ollama", is_available: true },
      { id: "llama3.1:8b", name: "llama3.1:8b", provider: "ollama", is_available: true },
      { id: "mistral:7b-instruct", name: "mistral:7b-instruct", provider: "ollama", is_available: true },
    ];
  }
}

async function loadBrandVoice() {
  if (!props.clientId) return;
  try {
    brandVoice.value = await aiSectionEditService.getBrandVoice(props.clientId);
  } catch {
    brandVoice.value = "";
  }
}

async function saveBrandVoice() {
  if (!props.clientId) return;
  savingBrandVoice.value = true;
  try {
    await aiSectionEditService.updateBrandVoice(props.clientId, brandVoice.value);
    toast.success("Client Brand Voice saved!");
  } catch (err: any) {
    toast.error(err.response?.data?.message || "Failed saving brand voice");
  } finally {
    savingBrandVoice.value = false;
  }
}

async function loadHistory() {
  if (!props.reportId || !props.sectionKey) return;
  loadingHistory.value = true;
  try {
    history.value = await aiSectionEditService.getHistory(props.reportId, props.sectionKey);
  } catch {
    history.value = [];
  } finally {
    loadingHistory.value = false;
  }
}

async function handleApply() {
  if (!props.reportId || !props.sectionKey || !promptText.value.trim() || isProcessing.value) {
    return;
  }

  if (props.isPublished) {
    openConfirm({
      title: "Update Published Report?",
      description: "This report is published. Applying this AI revision will update the live client report and PDF immediately. Proceed?",
      confirmLabel: "Apply to Live Report",
      confirmVariant: "warning",
      onConfirm: () => void executeApply(),
    });
    return;
  }

  await executeApply();
}

async function executeApply() {
  if (!props.reportId || !props.sectionKey || !promptText.value.trim()) return;

  isProcessing.value = true;
  errorMessage.value = null;
  fallbackNotice.value = null;
  processingStatusMessage.value = "Submitting revision...";

  const selectedModel = availableModels.value.find((m) => m.id === selectedModelId.value);
  const provider = selectedModel?.provider || "openai";

  try {
    const queueRes = await aiSectionEditService.queueSectionEdit(
      props.reportId,
      props.sectionKey,
      {
        prompt: promptText.value.trim(),
        provider,
        model: selectedModelId.value,
      }
    );

    const runId = queueRes.run_id;
    processingStatusMessage.value = "Consulting AI engine...";

    let pollCount = 0;
    const maxPolls = 60;
    const interval = setInterval(async () => {
      pollCount++;
      try {
        const run = await aiSectionEditService.pollRun(runId);

        if (run.status === "running") {
          processingStatusMessage.value = "Generating and validating section text...";
        }

        if (run.status === "succeeded") {
          clearInterval(interval);
          isProcessing.value = false;
          promptText.value = "";

          if (run.fallback_used && run.used_model) {
            fallbackNotice.value = `Requested model was unavailable. ${run.used_model} was automatically used.`;
          }

          toast.success("Section revised successfully!");
          emit("section-updated", props.sectionKey);
          await loadHistory();
        } else if (run.status === "failed") {
          clearInterval(interval);
          isProcessing.value = false;
          errorMessage.value = run.error_message || "Revision failed to complete.";
        } else if (pollCount >= maxPolls) {
          clearInterval(interval);
          isProcessing.value = false;
          errorMessage.value = "AI revision timed out. Please try again.";
        }
      } catch (err: any) {
        clearInterval(interval);
        isProcessing.value = false;
        errorMessage.value = err.response?.data?.message || "Failed communicating with server.";
      }
    }, 1500);
  } catch (err: any) {
    isProcessing.value = false;
    errorMessage.value = err.response?.data?.message || "Could not start revision.";
  }
}

async function handleUndo() {
  if (!props.reportId || !props.sectionKey || isProcessing.value) return;
  isProcessing.value = true;
  try {
    const res = await aiSectionEditService.undo(props.reportId, props.sectionKey);
    toast.success(res.message || "Reverted successfully!");
    emit("section-updated", props.sectionKey);
    await loadHistory();
  } catch (err: any) {
    toast.error(err.response?.data?.message || "Failed undoing edit.");
  } finally {
    isProcessing.value = false;
  }
}

function handleReset() {
  if (!props.reportId || !props.sectionKey || isProcessing.value) return;
  openConfirm({
    title: "Reset Section to Original?",
    description: "Are you sure you want to reset this section to the original generated report data? All AI edits for this section will be deactivated.",
    confirmLabel: "Reset to Original",
    confirmVariant: "danger",
    onConfirm: () => void executeReset(),
  });
}

async function executeReset() {
  if (!props.reportId || !props.sectionKey) return;
  isProcessing.value = true;
  try {
    const res = await aiSectionEditService.reset(props.reportId, props.sectionKey);
    toast.success(res.message || "Reset to original data.");
    emit("section-updated", props.sectionKey);
    await loadHistory();
  } catch (err: any) {
    toast.error(err.response?.data?.message || "Failed resetting section.");
  } finally {
    isProcessing.value = false;
  }
}

function handleUndoEnhance() {
  if (originalPromptBeforeEnhance.value !== null) {
    promptText.value = originalPromptBeforeEnhance.value;
    originalPromptBeforeEnhance.value = null;
    toast.info("Restored original prompt.");
  }
}

async function handleEnhancePrompt() {
  if (!promptText.value.trim() || !props.reportId || isProcessing.value || isEnhancing.value) return;
  isEnhancing.value = true;
  try {
    const rawPrompt = promptText.value;
    const enhanced = await aiSectionEditService.enhancePrompt(
      props.reportId,
      props.sectionKey,
      rawPrompt,
      undefined,
      selectedModelId.value
    );
    if (enhanced && enhanced.trim()) {
      originalPromptBeforeEnhance.value = rawPrompt;
      promptText.value = enhanced.trim();
      toast.success("Prompt enhanced with AI!", { position: "top-right" });
    }
  } catch (err: any) {
    toast.error(err?.response?.data?.message || "Failed to enhance prompt.", { position: "top-right" });
  } finally {
    isEnhancing.value = false;
  }
}

function handleRestoreVersion(version: number) {
  if (!props.reportId || !props.sectionKey || isProcessing.value) return;
  openConfirm({
    title: `Restore Version ${version}?`,
    description: `This will switch the active section content, layout, and theme to Version ${version}.`,
    confirmLabel: `Restore Version ${version}`,
    confirmVariant: "primary",
    onConfirm: async () => {
      isProcessing.value = true;
      processingStatusMessage.value = `Restoring Version ${version}...`;
      try {
        const res = await aiSectionEditService.restoreVersion(props.reportId!, props.sectionKey, version);
        toast.success(res.message || `Restored to Version ${version}`, { position: "top-right" });
        emit("section-updated", props.sectionKey);
        await loadHistory();
      } catch (err: any) {
        toast.error(err?.response?.data?.message || "Failed to restore version.", { position: "top-right" });
      } finally {
        isProcessing.value = false;
      }
    },
  });
}

function formatDate(isoDate?: string): string {
  if (!isoDate) return "";
  try {
    const d = new Date(isoDate);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) + " " + d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch {
    return isoDate;
  }
}

watch(
  () => [props.open, props.sectionKey],
  ([val]) => {
    if (val) {
      loadModels();
      loadBrandVoice();
      loadHistory();
      errorMessage.value = null;
      fallbackNotice.value = null;
    }
  }
);

onMounted(() => {
  if (props.open) {
    loadModels();
    loadBrandVoice();
    loadHistory();
  }
});
</script>
